// src/.../Hooks/useSelectQuestions.ts
import { useEffect, useState } from 'react';
import { searchPaginatedQuestions } from '../../../../../../../utils/api_service/job_api';
import {
  Question,
  QuestionSearch,
} from '../../../../../../../utils/types/assessment_types';

interface IUseSelectQuestions {
  searchObject: QuestionSearch;
  initialSelected: Question[];
}

export const useSelectQuestions = ({
  searchObject,
  initialSelected,
}: IUseSelectQuestions) => {
  // — paginated list you fetch
  const [questionsList, setQuestionsList] = useState<Question[]>([]);
  // — selection *inside the drawer*
  const [selectedQuestions, setSelectedQuestions] =
    useState<Question[]>(initialSelected);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // pagination
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  const totalPages = Math.ceil(totalCount / pageSize);

  // whenever filters change, reset to page 1
  useEffect(() => {
    setPageNumber(1);
  }, [searchObject]);

  // keep hook’s selectedQuestions in sync if parent’s initial changes
  useEffect(() => {
    setSelectedQuestions(initialSelected);
  }, [initialSelected]);

  // fetch current page
  useEffect(() => {
    const debounce = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await searchPaginatedQuestions({
          ...searchObject,
          pageNumber,
          questionPage: pageSize,
        });
        setQuestionsList(resp.questions);
        setTotalCount(resp.numberOfQuestions);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchObject, pageNumber]);

  // selection handlers inside drawer
  const handleSelect = (q: Question) => {
    if (!selectedQuestions.some((x) => x.id === q.id)) {
      setSelectedQuestions((prev) => [...prev, q]);
      setQuestionsList((prev) => prev.filter((x) => x.id !== q.id));
    }
  };
  const handleRemove = (q: Question) => {
    setSelectedQuestions((prev) => prev.filter((x) => x.id !== q.id));
    setQuestionsList((prev) => [...prev, q]);
  };
  const handleSelectAll = () => {
    setSelectedQuestions((prev) => [...prev, ...questionsList]);
    setQuestionsList([]);
  };
  const handleRemoveAll = () => {
    setQuestionsList((prev) => [...prev, ...selectedQuestions]);
    setSelectedQuestions([]);
  };

  const goToNextPage = () => setPageNumber((p) => Math.min(p + 1, totalPages));
  const goToPrevPage = () => setPageNumber((p) => Math.max(p - 1, 1));

  return {
    questionsList,
    selectedQuestions,
    loading,
    totalCount,
    pageNumber,
    totalPages,
    handleSelect,
    handleRemove,
    handleSelectAll,
    handleRemoveAll,
    goToNextPage,
    goToPrevPage,
    setIsSaving,
    isSaving,
  };
};
